import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Check, Trash2, ArrowUp, ArrowDown } from "lucide-react";

export default function Issues() {
  const [issues, setIssues] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", priority: 3 });

  const load = () => api.get("/issues").then((r) => setIssues(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) return;
    await api.post("/issues", { ...form, priority: Number(form.priority), status: "open" });
    setOpen(false); setForm({ title: "", description: "", priority: 3 });
    toast.success("Issue captured.");
    load();
  };

  const move = async (i, delta) => {
    const p = Math.max(1, Math.min(5, i.priority + delta));
    await api.put(`/issues/${i.id}`, { ...i, priority: p });
    load();
  };

  const solve = async (i) => {
    await api.put(`/issues/${i.id}`, { ...i, status: i.status === "open" ? "solved" : "open" });
    load();
  };

  const remove = async (id) => { if (confirm("Delete this issue?")) { await api.delete(`/issues/${id}`); load(); } };

  const open_issues = issues.filter((i) => i.status === "open");
  const solved = issues.filter((i) => i.status === "solved");

  const list = (arr, label) => (
    <div>
      <div className="mono text-xs uppercase text-neutral-500 tracking-widest mb-3">{label} · {arr.length}</div>
      <div className="border border-neutral-200 bg-white">
        {arr.length === 0 && <div className="p-8 text-center text-neutral-500 text-sm">Nothing here.</div>}
        {arr.map((i) => (
          <div key={i.id} className={`border-b border-neutral-100 last:border-b-0 p-4 flex items-start gap-3 hover:bg-neutral-50 ${i.status === "solved" ? "opacity-60" : ""}`} data-testid={`issue-row-${i.id}`}>
            <div className="mono text-xs text-neutral-500 pt-1 w-6">P{i.priority}</div>
            <div className="flex-1 min-w-0">
              <div className={`font-medium ${i.status === "solved" ? "line-through" : ""}`}>{i.title}</div>
              {i.description && <div className="text-sm text-neutral-600 mt-1">{i.description}</div>}
            </div>
            {i.status === "open" && (
              <div className="flex items-center gap-1">
                <button onClick={() => move(i, -1)} className="p-1 hover:text-[#E54D2E]" data-testid={`issue-up-${i.id}`}><ArrowUp className="h-3.5 w-3.5" /></button>
                <button onClick={() => move(i, 1)} className="p-1 hover:text-[#E54D2E]" data-testid={`issue-down-${i.id}`}><ArrowDown className="h-3.5 w-3.5" /></button>
              </div>
            )}
            <button onClick={() => solve(i)} className={`px-2 py-1 text-xs rounded-sm ${i.status === "open" ? "bg-green-600 text-white" : "bg-neutral-200"}`} data-testid={`issue-solve-${i.id}`}>
              <Check className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => remove(i.id)} className="text-neutral-400 hover:text-red-600" data-testid={`issue-delete-${i.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-start justify-between">
        <div>
          <div className="mono text-xs uppercase tracking-widest text-neutral-500">Module · Issues List</div>
          <h1 className="font-display text-4xl font-bold mt-2">Issues (IDS)</h1>
          <p className="text-neutral-600 mt-2">Identify · Discuss · Solve. Rank by priority and knock them down weekly.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#E54D2E] hover:bg-[#C93E22] text-white rounded-sm btn-lift" data-testid="issues-add-btn"><Plus className="h-4 w-4 mr-1" /> Add Issue</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add Issue</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Title</Label><Input value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} className="rounded-sm" data-testid="issue-title-input" /></div>
              <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} className="rounded-sm" data-testid="issue-desc-input" /></div>
              <div><Label>Priority (1 highest — 5 lowest)</Label><Input type="number" min={1} max={5} value={form.priority} onChange={(e)=>setForm({...form,priority:e.target.value})} className="rounded-sm" data-testid="issue-priority-input" /></div>
              <Button onClick={create} className="w-full bg-neutral-900 hover:bg-neutral-800 rounded-sm" data-testid="issue-create-submit">Add</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 space-y-8">
        {list(open_issues, "Open")}
        {list(solved, "Solved")}
      </div>
    </div>
  );
}
